/** @jsx jsx */
import {
  useCallback,
  ChangeEventHandler,
  useState,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { jsx } from '@emotion/core';
import { InputAdornment, IconButton, TextField } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Autocomplete from 'components/Autocomplete';
import { selectTenant, CardSelectorOption } from 'state/tenant';
import useTranslate from 'hooks/useTranslate/useTranslate';
import Clear from '@material-ui/icons/Clear';
import { selectLockedFields } from 'state/creditApplication';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useAnalytics, { Event } from 'hooks/useAnalytics';

interface PropTypes {
  selectedCardKey: string;
  onSelect?: (selectedCardKey: string) => void;
  id?: string;
}

const CardSelectorWidget: React.FC<PropTypes> = props => {
  const {
    enabledFeatures: { cardSelector },
  } = useSelector(selectTenant);
  const { setAnalyticsVariables } = useAnalyticsVariables();
  const { sendEvents } = useAnalytics();
  const { selectedCardKey: isSelectedCardKeyLocked } = useSelector(
    selectLockedFields,
  );

  if (!cardSelector) {
    // Should never happens
    throw new Error('Invalid tenant');
  }

  const options = cardSelector.options;
  const { selectedCardKey, onSelect, id } = props;

  const trackCardChanges = useCallback(
    (selectedCard: CardSelectorOption) => {
      if (
        window.digitalData.application.selectedCardName !== selectedCard.name
      ) {
        setAnalyticsVariables({ selectedCardName: selectedCard.name });
        sendEvents([Event.SelectedCardChanged]);
      }
    },
    [sendEvents, setAnalyticsVariables],
  );

  const initialCard = useMemo(
    () => options.find(option => option.key === selectedCardKey),
    [options, selectedCardKey],
  );

  useEffect(() => {
    if (initialCard) {
      trackCardChanges(initialCard);
    }
  }, [initialCard, trackCardChanges]);

  const [value, setValue] = useState<string>(
    (initialCard && initialCard.name) || '',
  );

  const t = useTranslate();

  const onSuggestionSelect = useCallback(
    (result: CardSelectorOption | undefined) => {
      if (result === undefined) {
        return;
      }

      onSelect && onSelect(result.key);
    },
    [onSelect],
  );

  const suggestions = useMemo(() => {
    if (value) {
      return Array.from(
        new Set(
          options
            .filter(suggestion =>
              suggestion.name.toLowerCase().startsWith(value.toLowerCase()),
            )
            .concat(
              options.filter(suggestion =>
                suggestion.name.toLowerCase().includes(value.toLowerCase()),
              ),
            ),
        ),
      );
    } else {
      const suggestions = options.sort((firstItem, secondItem) =>
        firstItem.name.localeCompare(secondItem.name),
      );
      return initialCard
        ? [
            initialCard,
            ...suggestions.filter(
              suggestion => suggestion.name !== initialCard.name,
            ),
          ]
        : suggestions;
    }
  }, [initialCard, options, value]);

  const inputRef = useRef<HTMLElement | null>(null);

  return (
    <Autocomplete
      id={id || 'selectedCardKey'}
      inputValue={value}
      onInputValueChange={value => {
        setValue(value);
      }}
      initialSelectedItem={initialCard}
      suggestions={suggestions}
      itemToString={item => (item ? item.name : '')}
      onSelect={result => {
        onSuggestionSelect(result);
        if (inputRef.current) inputRef.current.blur();
      }}
      noSuggestions={
        suggestions.length === 0
          ? { message: t('cardSelector.noSuggestionsMessage') }
          : undefined
      }
      shouldBoldSelectedItem={true}
      overflow
    >
      {({
        getInputProps,
        getLabelProps,
        closeMenu,
        openMenu,
        isOpen,
        inputRef: anchorRef,
        getRootProps,
      }) => {
        // Callback needs to be passed to the component
        // to compose with its own callbacks.
        // The rest of the props (mostly aria-* attributes)
        // needs to be on the actual input itself. Otherwise,
        // TypeScript gets confused (probably have something to
        // do with the way TextField is typed by MUI)
        const { onBlur, onChange, onKeyDown, ...inputProps } = getInputProps();

        return (
          <TextField
            disabled={isSelectedCardKeyLocked}
            ref={anchorRef}
            inputRef={inputRef}
            label={t('cardSelector.cardSelectionLabel')}
            onFocus={() => {
              openMenu();
              // Clear button relies on clearing when focused
              setValue('');
            }}
            fullWidth
            onChange={onChange as ChangeEventHandler<HTMLInputElement>}
            onKeyDown={onKeyDown}
            inputProps={{
              ...inputProps,
              maxLength: 30,
            }}
            InputProps={{
              endAdornment:
                value === '' || !isOpen ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="expand"
                      edge="end"
                      onClick={() => {
                        if (inputRef.current) {
                          if (isOpen) {
                            if (initialCard) setValue(initialCard.name);
                            closeMenu();
                            inputRef.current.blur();
                          } else inputRef.current.focus();
                        }
                      }}
                    >
                      {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear"
                      onFocus={() => {
                        // onFocus will clear input
                        setValue('');
                        if (inputRef.current) inputRef.current.focus();
                      }}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              ...getRootProps(
                { refKey: 'ref' },
                // This will spread the ref from Downshift into the
                // Input component, which forward it to the root element,
                // which should be the div that wrap the input element.
                // This is correct, but since the root element return to
                // Downshift is not the div that wraps the input
                // element, Downshift will raise a ref error.
                // According to the documentation, we can suppress
                // the ref error in this case.
                { suppressRefError: true },
              ),
            }}
            InputLabelProps={{ ...getLabelProps(), required: false }}
          />
        );
      }}
    </Autocomplete>
  );
};
export default CardSelectorWidget;
